import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, FilterX, BookOpen, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { postAPI } from '../utils/api';
import PostCard from '../components/PostCard';
import CreatePostModal from '../components/CreatePostModal';
import ConfirmModal from '../components/ConfirmModal';

const Posts = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deletingConfirm, setDeletingConfirm] = useState(false);
  const limit = 12;

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchPosts = useCallback(async (pageToFetch = page) => {
    try {
      setLoading(true);
      const params = { page: pageToFetch, limit };
      if (searchDebounced) params.search = searchDebounced;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const response = await postAPI.getAll(params);
      setPosts(response.data.data || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error(t('installationInstructions.errors.fetchList'));
    } finally {
      setLoading(false);
    }
  }, [searchDebounced, dateFrom, dateTo, page, t]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchPosts(page);
      return;
    }
    setPage(1);
    fetchPosts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounced, dateFrom, dateTo]);

  useEffect(() => {
    fetchPosts(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleResetFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
    toast.success(t('installationInstructions.resetFilters'));
  };

  const handleCreate = async (formData) => {
    setCreating(true);
    try {
      await postAPI.create(formData);
      toast.success(t('installationInstructions.success.create'));
      setShowModal(false);
      setPage(1);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.message || t('installationInstructions.errors.create'));
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (formData) => {
    setUpdating(true);
    try {
      await postAPI.update(editing._id, formData);
      toast.success(t('installationInstructions.success.update'));
      setEditing(null);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.message || t('installationInstructions.errors.update'));
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeletingConfirm(true);
    try {
      await postAPI.delete(deleting._id);
      toast.success(t('installationInstructions.success.delete'));
      setDeleting(null);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.message || t('installationInstructions.errors.delete'));
    } finally {
      setDeletingConfirm(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('installationInstructions.title')}</h1>
          <p className="text-gray-500 text-sm mt-1">{t('installationInstructions.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center"
        >
          <Plus className="w-4 h-4" />
          {t('installationInstructions.createButton')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <FilterX className="w-4 h-4" />
            {t('installationInstructions.filters.title')}
          </h3>
          <button
            onClick={handleResetFilters}
            className="text-xs text-gray-500 hover:text-primary-600 flex items-center gap-1"
          >
            <FilterX className="w-3.5 h-3.5" />
            {t('installationInstructions.resetFilters')}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {t('installationInstructions.filters.search')}
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="form-input"
              placeholder={t('installationInstructions.filters.searchPlaceholder')}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {t('installationInstructions.filters.dateFrom')}
            </label>
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {t('installationInstructions.filters.dateTo')}
            </label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              className="form-input"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm text-gray-500 bg-white px-4 py-2 rounded-lg border border-gray-200">
        <BookOpen className="w-4 h-4" />
        <span>
          {t('installationInstructions.total', { count: total })}
        </span>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
          <p className="text-gray-500 mt-2">{t('common.loading')}</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium text-gray-600">
            {t('installationInstructions.empty.title')}
          </p>
          <p className="text-sm mt-1 text-gray-400">
            {t('installationInstructions.empty.hint')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map((post) => (
            <PostCard
              key={post._id}
              post={post}
              linkTo={`/posts/${post._id}`}
              onEdit={setEditing}
              onDelete={setDeleting}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            type="button"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="btn-secondary"
          >
            {t('installationInstructions.back')}
          </button>
          <span className="text-sm text-gray-600 px-4">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="btn-secondary"
          >
            {t('installationInstructions.next')}
          </button>
        </div>
      )}

      <CreatePostModal
        isOpen={showModal || !!editing}
        onClose={() => {
          setShowModal(false);
          setEditing(null);
        }}
        onSubmit={editing ? handleUpdate : handleCreate}
        initialData={editing}
        submitting={editing ? updating : creating}
      />

      <ConfirmModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={t('installationInstructions.confirm.title')}
        message={t('installationInstructions.confirm.message')}
        confirmText={t('installationInstructions.confirm.confirmText')}
        cancelText={t('installationInstructions.confirm.cancelText')}
        submitting={deletingConfirm}
      />
    </div>
  );
};

export default Posts;
